import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';
import { merge } from 'lodash-es';
import { TbConfirmationConfig } from './tb-confirmation.types';

/**
 * Service that extends the functionality of FuseConfirmationService by adding the ability
 * to specify a custom panel class for the confirmation dialog.
 *
 * While maintaining all the features of FuseConfirmationService (configurable title, message,
 * icons, and actions), this service adds the flexibility to customize the dialog's appearance
 * through custom CSS classes via the panelClass property in the configuration.
 */
@Injectable({ providedIn: 'root' })
export class TbConfirmationService {
    /**
     * Dialog service injected to handle opening/closing dialogs
     */
    private _matDialog: MatDialog = inject(MatDialog);

    /**
     * Default configuration for the confirmation dialog.
     * These values can be overridden when calling the open() method.
     */
    private _defaultConfig: TbConfirmationConfig = {
        title: 'Confirm action',
        message: 'Are you sure you want to confirm this action?',
        icon: {
            show: true,
            name: 'heroicons_outline:exclamation-triangle',
            color: 'warn',
        },
        actions: {
            confirm: {
                show: true,
                label: 'Confirm',
                color: 'warn',
            },
            cancel: {
                show: true,
                label: 'Cancel',
            },
        },
        dismissible: false,
    };

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Opens a confirmation dialog with the provided configuration.
     * Extends FuseConfirmationService's functionality by allowing a custom panelClass to be specified.
     *
     * @param config Optional configuration to override default settings. Can include a panelClass property
     * to customize the dialog's appearance through CSS.
     * @returns A MatDialogRef instance for the opened dialog
     */
    open(config: TbConfirmationConfig = {}): MatDialogRef<FuseConfirmationDialogComponent> {
        // Merge the user config with the default config
        const userConfig = merge({}, this._defaultConfig, config);

        // Open the dialog with optional custom panel class
        return this._matDialog.open(FuseConfirmationDialogComponent, {
            autoFocus: false,
            disableClose: !userConfig.dismissible,
            data: userConfig,
            panelClass: userConfig.panelCass ? userConfig.panelCass : 'fuse-confirmation-dialog-panel',
        });
    }
}
